<template>
    <div id="chatCloseSettingsButton">
        <VisitorBanModal
            v-if="visitorBanModal"
            :visitor="contact"
            :ban-modal-visible="visitorBanModal"
            @changed="visitorBanUpdated"
            @close="visitorBanModal = false"
        />
        <PrivateMessage
            v-if="privateMessage"
            :conversation-id="contact.lastConversation._id"
            @close="privateMessage = false"
        />
        <Modal2
            v-if="assignAgent"
            id="assign-agent"
            :title="$t('message.addRepresentative')"
            :show-footer="false"
            @cancel="assignAgent = false"
        >
            <AgentList :assign="true" :show-online-agents="true" @close="() => closeModal('assign')" />
        </Modal2>
        <Modal2
            v-if="forwardAgent"
            id="forward-agent"
            :title="$t('message.forwardRepresentative')"
            :show-footer="false"
            @cancel="forwardAgent = false"
        >
            <AgentList :forward="true" :show-online-agents="true" @close="() => closeModal('forward')" />
        </Modal2>
        <div class="user-meta">
            <ContactAvatar :contact="contact" :size="'smallest'" />
            <div class="contact-details">
                <div
                    ref="visitorNameInput"
                    class="contact-detail visitor-name"
                    @keydown="handleVisitorNameInputKeyDown"
                    @keyup="handleVisitorNameInputKeyUp"
                    placeholder="Type visitor name..."
                >
                    {{ getFullname() }}
                </div>
                <div class="contact-detail visitor-email" v-if="contact.email && contact.email !== ''">
                    <div class="material-icons">mail</div>
                    <div ref="visitorEmailInput" @keydown="handleVisitorEmailInputKeyDown">
                        <span v-if="hasUserMail()">
                            <a :href="'mailto:' + getEmail()">{{ getEmail() }}</a>
                        </span>
                        <span v-else>
                            <a :href="'tel:' + getChannelNumber()">{{ getChannelNumber() }}</a>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="conversation-actions">
            <SupsisButton v-if="isShowing('join-conversation')" primary> {{ $t("chatWindow.watching") }}</SupsisButton>
            <div
                class="call-features"
                v-else-if="
                    isShowing('call-features') &&
                    (videoCallFeatureEnabled || audioCallFeatureEnabled) &&
                    !videoCallMode &&
                    !contact.whatsappNumber &&
                    !contact.instagramUserId &&
                    !contact.messengerUserId &&
                    !contact.telegramUserId
                "
            >
                <div class="conversation-actions-button call" @click="call(true)" v-if="audioCallFeatureEnabled">
                    <div class="material-icons">call</div>
                </div>
                <div class="conversation-actions-button video-call" @click="call(false)" v-if="videoCallFeatureEnabled">
                    <div class="material-icons">video_call</div>
                </div>
            </div>

            <a-popover
                overlayClassName="actionSettingsButton"
                v-model="isPopoverVisible"
                data-v-step="17"
                placement="bottomRight"
                title=""
                trigger="click"
            >
                <template #content>
                    <div class="more-actions" v-if="user">
                        <div
                            class="action-button"
                            v-if="checkPermission(PERMISSION_TYPES['visitor.block'].tr.name)"
                            @click="ban"
                        >
                            <div class="action-button-icon">
                                <div class="material-icons">block</div>
                            </div>
                            <div class="action-button-text">{{ $t("message.block_") }}</div>
                        </div>
                        <div class="action-button" v-if="isShowing('assign-agent')" @click="assignRep">
                            <div class="action-button-icon">
                                <div class="material-icons">person_add</div>
                            </div>
                            <div class="action-button-text">{{ $t("message.assignRep") }}</div>
                        </div>
                        <template v-else>
                            <div class="action-button" @click="pm" v-if="isShowing('private-message')">
                                <div class="action-button-icon">
                                    <div class="material-icons">mail_lock</div>
                                </div>
                                <div class="action-button-text">{{ $t("message.privateMessage_") }}</div>
                            </div>
                            <div class="action-button" v-if="isShowing('forward-agent')" @click="forwardRep">
                                <div class="action-button-icon">
                                    <div class="material-icons">person_4</div>
                                </div>
                                <div class="action-button-text">{{ $t("message.forwardRep") }}</div>
                            </div>
                        </template>
                        <div class="action-button" v-if="isShowing('auto-translate')">
                            <div class="action-button-icon">
                                <div class="material-icons">translate</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.autoTranslate") }} :
                                <a-switch
                                    :checked="autoTranslateEnabled"
                                    @change="$emit('autoTranslateChanged', $event)"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button" v-if="isShowing('leave-chat')" @click="leave">
                            <div class="action-button-icon">
                                <div class="material-icons">logout</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.leaveChat") }}
                            </div>
                        </div>
                        <div class="action-button end-chat-button" @click="end" v-if="isShowing('destory-chat')">
                            <div class="action-button-icon">
                                <div class="material-icons">power_settings_new</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.endChat") }}
                            </div>
                        </div>
                    </div>
                </template>
                <div class="conversation-actions-button more-vert">
                    <div class="material-icons">more_vert</div>
                </div>
            </a-popover>
        </div>
    </div>
</template>
<script>
import CircleCloseButton from "../../svg/CircleCloseButton.vue";
import CONFIG from "../../../config/config";
import { getPersistenceData } from "../../../utils/persistenceStorage";
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { PHONE_UTILS } from "../../../utils/phone";
import { log2 } from "../../../utils/log2";
import { PHONE } from "../../../webrtc/phoneSDK";
import { sendSocketMessage } from "../../../utils/socketStream";
import { publishMessage } from "../../../utils/systemPubSub";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import { capitalize, fakeConversation, SanitizeHtml } from "../../../utils/utils";
import VideoCallCloseSvg from "../../svg/VideoCallCloseSvg.vue";
import PowerOff from "../../svg/PowerOff.vue";
import Block from "../../../views/Pages/Chatbot/pages/dashboard/diagram/Block.vue";
import AssignRepresentative from "../../svg/AssignRepresentative.vue";
import OpenChat from "../../svg/OpenChat.vue";
import ForwardRepresentative from "../../svg/ForwardRepresentative.vue";
import MinimizeWindow from "../../svg/MinimizeWindow.vue";
import ChatWindowSettings from "../../svg/ChatWindowSettings";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import VisitorBanModal from "../../VisitorBanModal.vue";
import Modal2 from "../../common/Modal2.vue";
import PrivateMessage from "../../PrivateMessage.vue";
import AgentList from "../../AgentList.vue";
import ContactAvatar from "./ContactAvatar.vue";

const log = log2("ChatCloseSettingsButton");
export default {
    name: "ChatCloseSettingsButton",
    components: {
        ContactAvatar,
        AgentList,
        PrivateMessage,
        Modal2,
        VisitorBanModal,
        SupsisButton,
        MinimizeWindow,
        ForwardRepresentative,
        OpenChat,
        AssignRepresentative,
        Block,
        CircleCloseButton,
        VideoCallCloseSvg,
        PowerOff,
        ChatWindowSettings,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        autoTranslateEnabled: {
            type: Boolean,
            default: false,
        },
        isWatching: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            isRoot: (state) => state.auth.user.role.isRoot,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            videoCallMode: (state) => state.webrtc.videoCallMode,
            videoCallFeatureEnabled: (state) => _.get(state, "setting.settings.videocallEnabled", true),
            audioCallFeatureEnabled: (state) => _.get(state, "setting.settings.audiocallEnabled", true),
            settings: (state) => state.setting.settings,
            CustomLoginInputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
            isDarkMode: "user/isDarkMode",
        }),
    },
    data: () => ({
        isPopoverVisible: false,
        visitorBanModal: false,
        privateMessage: false,
        assignAgent: false,
        forwardAgent: false,
        PHONE_UTILS,
    }),
    methods: {
        ...mapActions({
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            viewFakeConversation: "chat/viewFakeConversation",
            multiBeepConversation: "chat/multiBeepConversation",
            joinContactConversation: "chat/joinContactConversation",
            leaveContactConversation: "chat/leaveContactConversation",
            destroyContactConversation: "chat/destroyContactConversation",
            hideConversation: "chat/hideConversation",
            setVideoCallMode: "webrtc/setVideoCallMode",
            setCalling: "webrtc/setCalling",
            updateMultipleKeyContact: "chat/updateMultipleKeyContact",
        }),
        closeModal(modal) {
            log("closeModal", modal);
            if (modal === "assign") {
                this.assignAgent = false;
            } else if (modal === "forward") {
                this.forwardAgent = false;
            }
        },
        capitalize,
        isShowing(key) {
            let showing = true;
            const isLeak = this.contact.lastConversation.users.length === 0;
            if (key === "join-conversation") {
                showing = this.isWatching;
            } else if (key === "assign-agent") {
                showing = this.isWatching && isLeak;
            } else if (key == "forward-agent") {
                showing = !this.isWatching && !isLeak && this.contact.lastConversation.status !== "destroyed";
            } else if (key === "leave-chat") {
                showing = !this.isWatching && this.contact.lastConversation.users.length > 1;
            } else if (key === "call-features") {
                showing =
                    this.contact.platform === "web" &&
                    !this.isWatching &&
                    this.contact.lastConversation.status !== "destroyed";
            } else if (key === "destory-chat") {
                showing = !this.isWatching && this.contact.lastConversation.status !== "destroyed";
            } else if (key === "more-vertical") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "private-message") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "auto-translate") {
                showing =
                    !this.isWatching && this.autoTranslate && this.contact.lastConversation.status !== "destroyed";
            }
            return showing;
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        getVisitorAvatarSrc() {
            if (this.contact?.lastConversation?.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${
                    this.contact?.lastConversation?.whatsappNumber
                }&siteId=${this.site._id}&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            } else if (this.contact?.lastConversation?.instagramUserId) {
                return _.get(this.contact, "instagramInfo.profile_pic");
            } else if (this.contact.messengerUserId) {
                return _.get(this.contact, "messengerInfo.profile_pic");
            }
            return undefined;
        },
        handleVisitorNameInputKeyUp(event) {
            const el = this.$refs.visitorNameInput;
            let fullname = el.textContent.toString();
            const input = String.fromCharCode(event.keyCode);
            const isValidInput = /[a-zA-Z0-9-_ ]/.test(input);
            const isEmpty = fullname.trim().length === 0;
            log("fullname keyup", { input, isValidInput, isEmpty, fullname }, event);
            if (isEmpty && isValidInput) {
                el.textContent = input;
                setTimeout(() => {
                    const end = el.textContent.length;
                    el.setSelectionRange(end, end);
                    el.focus();
                }, 100);
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        hasUserMail() {
            const email = this.getEmail();
            return !(!email || email.includes("@c.us"));
        },
        getChannelNumber() {
            return "+" + this.contact.email.split("@")[0];
        },
        getFullname() {
            let fullname = this.contact.fullname;
            if (!fullname) {
                fullname = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "fullname",
                    undefined
                );
            }
            return SanitizeHtml(fullname || this.contact.whatsappNumber || "-");
        },
        getEmail() {
            let email = this.contact.email;
            if (!email)
                email = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "email",
                    undefined
                );
            return SanitizeHtml(email, true);
        },
        getCustomLoginInputField(customLoginObject = [], requestedField, defaultValue) {
            let inputValues = customLoginObject
                .filter((x) => this.CustomLoginInputs[x.input])
                .map((v) => ({
                    value: v.value,
                    name: this.CustomLoginInputs[v.input].type,
                }));
            const input = inputValues.find((x) => x.name === requestedField);
            return _.get(input, "value", defaultValue);
        },
        call(soundOnly = false, screenOnly = false) {
            if (this.calling) {
                log.warn("video call request is not allowed , when another call request waiting");
                return;
            }
            log("call running");
            let config = {
                userId: this.user._id,
                secure: true,
                ssl: true,
                autocam: true,
            };
            if (!screenOnly) {
                let media = {
                    video: soundOnly ? false : { facingMode: "user" },
                    audio: true,
                };
                config = { media, ...config };
            }
            const phone = PHONE_UTILS.getPhone() || PHONE(config);
            phone.configUpdate(config);
            log("call running", { phone, soundOnly });

            PHONE_UTILS.setPhoneInstance(phone);
            phone.startcamera();
            this.setVideoCallMode({
                videoCallMode: true,
                conversationId: this.contact.lastConversation._id,
                visitorId: this.contact._id,
                soundOnly,
            });
            this.setCalling(true);
            sendSocketMessage("user video-call-request", {
                siteId: this.site._id,
                userId: this.user._id,
                visitorId: this.contact._id,
                conversationId: this.contact.lastConversation._id,
                soundOnly,
            });
        },
        cancelCall() {
            publishMessage("vc_ended", { signal: true });
        },
        assignRep() {
            this.isPopoverVisible = false;
            this.assignAgent = true;
        },
        forwardRep() {
            this.isPopoverVisible = false;
            this.forwardAgent = true;
        },
        pm() {
            this.isPopoverVisible = false;
            this.privateMessage = true;
        },
        ban() {
            this.isPopoverVisible = false;
            this.visitorBanModal = true;
        },
        joinTheChat(minimized = false, autoAssign = false) {
            if (this.fakeMode) {
                this.viewFakeConversation(fakeConversation());
                return;
            }
            this.multiBeepConversation([this.contact.lastConversation._id]);
            this.joinContactConversation(this.contact);
        },
        leave() {
            this.$dialog
                .confirm(this.$t("message.leaveChartConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.leaveContactConversation(this.contact);
                });
        },
        end() {
            this.$dialog
                .confirm(this.$t("message.endChatConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.stopVC();
                    this.destroyContactConversation(this.contact);
                });
        },
        stopVC() {
            log("cancelVideoCall", this.dialingVisitorId);
            if (this.dialingVisitorId && this.dialingConversationId) {
                const phone = PHONE_UTILS.getPhone();
                // dont member dialingConversationId
                sendSocketMessage("user video-call-close", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    visitorId: this.dialingVisitorId,
                    conversationId: this.dialingConversationId,
                    isSoundOnly: this.soundOnly,
                });

                if (this.videoCallMode) {
                    phone.camera.stop();
                    phone.hangup(this.dialingVisitorId);
                }
                this.setVideoCallMode({
                    videoCallMode: false,
                    conversationId: null,
                    visitorId: null,
                    soundOnly: false,
                });
                this.setCalling(false);
            }
        },
        visitorBanUpdated(suppression) {
            this.updateMultipleKeyContact({
                contactId: this.contact._id,
                set: {
                    suppression,
                },
            });
        },
    },
    watch: {
        contact: {
            deep: true,
            handler(oldVal, newVal) {
                console.log("contact has been changed", {
                    oldVal,
                    newVal,
                });
            },
        },
    },
};
</script>
<style lang="scss">
// Popover Styling
.actionSettingsButton {
    .ant-popover-arrow {
        display: none !important;
    }

    .ant-popover-inner-content {
        box-shadow: none !important;
        border: 1px solid var(--border-neutral-default);
        border-radius: 7px;
        padding: 0 !important;

        .more-actions {
            overflow: hidden;

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 10px;
                cursor: pointer;

                &:hover {
                    background: #f5f5f5;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #007aff;
                        }
                    }

                    .action-button-text {
                        color: #007aff;
                    }
                }

                .action-button-icon {
                    line-height: 0;

                    .material-icons {
                        font-size: 18px;
                        color: #4a4a4a;
                    }
                }

                .action-button-text {
                    font-size: 14px;
                    color: #4a4a4a;
                }
            }

            .action-button:not(:last-child) {
                border-bottom: 1px solid var(--border-neutral-default);
            }

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 4px;
                cursor: pointer;
                border: none !important;

                &:hover {
                    background: #f1f4f7;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #4a4a4a;
                        }
                    }

                    .action-button-text {
                        color: #4a4a4a;
                    }
                }
            }

            .end-chat-button {
                .action-button-icon {
                    .material-icons {
                        color: #fd6144 !important;
                    }
                }

                .action-button-text {
                    color: #fd6144 !important;
                }
            }
        }
    }
}
</style>
<style lang="scss" scoped>
#chatCloseSettingsButton {
    display: flex;
    justify-content: space-between;
    padding: 10px 10px 10px 10px;
    background-color: white;
    align-items: center;
    height: 50px;
    border-bottom: 1px solid var(--border-neutral-default);

    .user-meta {
        display: flex;
        align-items: center;
        gap: 5px;

        .contact-avatar {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            min-height: 40px !important;
            border-radius: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .contact-details {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .contact-detail {
                display: flex;
                align-items: center;
                gap: 2px;

                &.visitor-name {
                    font-size: 16px;
                    font-weight: bold;
                }

                &.visitor-email {
                    margin-top: -5px;

                    &,
                    a {
                        color: var(--color-text-info-default);
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }
    }

    .conversation-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        &-button {
            cursor: pointer;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
            user-select: none;

            .material-icons {
                color: var(--text-main-default);

                &:hover {
                }
            }

            &.call {
                .material-icons {
                    font-size: 20px;
                }
            }

            &.video-call {
                .material-icons {
                    font-size: 24px;
                }
            }

            &.more-vert {
                .material-icons {
                    font-size: 24px;
                }
            }
        }

        .call-features {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
}
</style>
